import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CalendarDays, Crown, Gem, Gift, Info, Package, RefreshCw, Sparkles, Trophy, Users } from 'lucide-react';
import { apiService, type BoxLotRowDTO, type BoxStatsResponse } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { formatPlayerName } from '@/utils/playerName';
import { ColumnChart, DonutChart, type ColumnDatum } from './JobsCharts';
import { dayLabel, formatInstant, shortDay } from './jobStats';
import { medal, shortUuid } from './moneyLeaderboard';
import {
  BOX_SATELLITE_VERSION, boxColor, boxLabel, expectedLabel, lotLabel, rarityColor, rarityLabel, rateLabel, verdict, type Verdict,
} from './boxStats';
import lb from './MoneyLeaderboardSection.module.scss';
import styles from './JobsStatsSection.module.scss';
import bx from './BoxSection.module.scss';

const PERIODS = [7, 14, 30, 90] as const;

/** Lignes du tableau des lots avant « Voir tous les lots » (le satellite en rend 80, légendaires d'abord). */
const LOT_ROWS = 20;

/**
 * Section « Box » de l'espace Statistiques : qui ouvre des box, ce qu'ils obtiennent, combien de lots légendaires
 * sortent comparés à ce que prévoient les poids des box. Lu par le satellite dans le journal des ouvertures de
 * PremiumMod 0.0.80. Les clés et box vendues à la boutique sont dans la section Boutique, avec les autres achats.
 */
export function BoxSection() {
  const gameId = useGameStore((s) => s.activeGameId);
  const [days, setDays] = useState<number>(30);
  const [box, setBox] = useState<string | null>(null);
  const [data, setData] = useState<BoxStatsResponse | null>(null);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [allLots, setAllLots] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getBoxStats(days, box);
    if (res.data) { setData(res.data); setError(null); }
    else {
      setData(null);
      setError({
        status: res.status,
        message: res.status === 404
          ? `Le satellite de ce projet ne sert pas encore les statistiques des box : cela arrive avec le satellite ${BOX_SATELLITE_VERSION}.`
          : res.status === 403 ? "Ton grade n'a pas accès à cette section des statistiques."
            : res.error?.trim() || "Les statistiques des box n'ont pas pu être lues.",
      });
    }
    setLoading(false);
  }, [days, box, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);
  // Un autre projet n'a pas les mêmes box.
  useEffect(() => { setBox(null); }, [gameId]);

  const allBoxIds = useMemo(() => (data?.boxes ?? []).map((b) => b.box), [data]);
  const names = useMemo(() => new Map((data?.boxes ?? []).map((b) => [b.box, boxLabel(b.box, b.name)])), [data]);
  const nameOf = (id: string) => names.get(id) ?? boxLabel(id);

  if (loading && !data && !error) return <div className={lb.state}><div className={lb.spinner} /><p>Lecture des box...</p></div>;
  if (error && !data) {
    return (
      <div className={lb.state}>
        {error.status === 404 ? <Info size={32} className={styles.stateIcon} /> : <AlertCircle size={32} />}
        <h3>{error.status === 404 ? 'Statistiques des box pas encore servies' : 'Box illisibles'}</h3>
        <p className={styles.stateText}>{error.message}</p>
        <button className={lb.buttonGhost} onClick={() => void load()}><RefreshCw size={16} /> Vérifier de nouveau</button>
      </div>
    );
  }
  if (!data) return null;
  if (!data.available) return <div className={lb.state}><Gift size={32} /><h3>Pas de journal des box ici</h3><p>{data.reason}</p></div>;

  const t = data.totals!;
  const legendaryVerdict = verdict(t.legendary, t.legendaryExpected);
  const cards = [
    { icon: Gift, value: t.openings.toLocaleString('fr-FR'), label: 'Box ouvertes', sub: `${t.draws.toLocaleString('fr-FR')} lots tirés` },
    { icon: Users, value: t.players.toLocaleString('fr-FR'), label: 'Joueurs qui ouvrent', sub: 'Joueurs distincts sur la période' },
    {
      icon: Crown, value: t.legendary.toLocaleString('fr-FR'), label: 'Lots légendaires',
      sub: t.legendaryExpected != null ? `${expectedLabel(t.legendaryExpected)} attendus d'après les poids` : 'Taux prévu inconnu',
    },
  ];

  const many = box == null && allBoxIds.length > 1;
  const openingCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: many
      ? allBoxIds.map((id) => ({ id, label: nameOf(id), value: d.byBox[id] ?? 0, color: boxColor(id, allBoxIds) }))
      : [{ id: 'o', label: 'Ouvertures', value: d.openings, color: box ? boxColor(box, allBoxIds) : 'var(--job-1)' }],
    note: `${d.draws.toLocaleString('fr-FR')} lots tirés`,
  }));
  const legendaryCols: ColumnDatum[] = data.daily.map((d) => ({
    key: d.date, label: shortDay(d.date), title: dayLabel(d.date),
    segments: [{ id: 'l', label: 'Légendaires', value: d.legendary, color: rarityColor(3) }],
  }));
  const count = (n: number) => n.toLocaleString('fr-FR');
  const lots = allLots ? data.lots : data.lots.slice(0, LOT_ROWS);

  return (
    <div className={styles.section}>
      <div className={lb.cards}>
        {cards.map((c) => (
          <div key={c.label} className={lb.card}>
            <div className={lb.cardIcon}><c.icon size={22} strokeWidth={2} /></div>
            <div className={lb.cardText}>
              <span className={lb.cardValue}>{c.value}</span>
              <span className={lb.cardLabel}>{c.label}</span>
              <span className={lb.cardSub}>{c.sub}</span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.info}><Info size={16} />
        <span>
          Ouvertures des {data.days} derniers jours, notées par le jeu à chaque box ouverte
          {data.since ? ` (première ouverture connue le ${formatInstant(data.since).split(' à ')[0]})` : ''}.
          Le taux prévu d'un lot est son poids dans le fichier de la box : il se compare au taux vu, tirage par tirage.
          {legendaryVerdict && ` Lots légendaires : ${legendaryVerdict.label.toLowerCase()}.`}
          {t.gone > 0 && ` ${count(t.gone)} ouvertures d'un joueur parti pendant l'animation : ses lots lui ont été donnés quand même.`}
          {' '}Les clés et box vendues à la boutique sont dans la section Boutique.
        </span>
      </div>

      <section className={styles.block}>
        <div className={styles.blockHead}>
          <h3><Package size={18} /> Box</h3>
          <select className={lb.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Période">
            {PERIODS.map((p) => <option key={p} value={p}>{p} jours</option>)}
          </select>
        </div>
        <div className={styles.chips} role="group" aria-label="Box affichée">
          <button className={`${styles.chip} ${box == null ? styles.chipActive : ''}`} onClick={() => setBox(null)}>Toutes les box</button>
          {data.boxes.map((b) => (
            <button key={b.box} className={`${styles.chip} ${box === b.box ? styles.chipActive : ''}`} onClick={() => setBox(b.box)} title={b.box}>
              <span className={styles.swatch} style={{ background: boxColor(b.box, allBoxIds) }} /> {nameOf(b.box)}
            </button>
          ))}
        </div>
        <div className={lb.tableWrap}>
          <table className={lb.table}>
            <thead>
              <tr>
                <th>Box</th><th className={lb.num}>Ouvertures</th><th className={lb.num}>Joueurs</th><th className={lb.num}>Lots tirés</th>
                <th className={lb.num}>Légendaires</th><th className={lb.num}>Attendus</th><th className={lb.num}>Taux vu</th>
                <th className={lb.num}>Taux prévu</th><th>Écart</th>
              </tr>
            </thead>
            <tbody>
              {data.boxes.map((b) => (
                <tr key={b.box}>
                  <td title={b.box}><span className={bx.lotCell}><span className={styles.swatch} style={{ background: boxColor(b.box, allBoxIds) }} />{nameOf(b.box)}</span></td>
                  <td className={lb.num}>{count(b.openings)}</td>
                  <td className={lb.num}>{count(b.players)}</td>
                  <td className={lb.num}>{count(b.draws)}</td>
                  <td className={lb.num}>{count(b.legendary)}</td>
                  <td className={lb.num}>{b.legendaryExpected != null ? expectedLabel(b.legendaryExpected) : '?'}</td>
                  <td className={lb.num}>{rateLabel(b.legendaryRate)}</td>
                  <td className={lb.num}>{rateLabel(b.legendaryExpectedRate)}</td>
                  <td><VerdictPill v={verdict(b.legendary, b.legendaryExpected)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.muted}>Taux vu et taux prévu des lots légendaires, à chaque lot tiré (un grade qui donne plusieurs lots par box tire plusieurs fois).</p>
      </section>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><CalendarDays size={18} /> Par jour</h3></div>
        <div className={styles.grid2}>
          <div>
            <h4 className={styles.blockSub}>Box ouvertes</h4>
            <ColumnChart data={openingCols} formatValue={count} ariaLabel="Box ouvertes par jour" height={190}
              legend={many ? allBoxIds.map((id) => ({ id, label: nameOf(id), color: boxColor(id, allBoxIds) })) : undefined} />
          </div>
          <div>
            <h4 className={styles.blockSub}>Lots légendaires sortis</h4>
            <ColumnChart data={legendaryCols} formatValue={count} ariaLabel="Lots légendaires sortis par jour" height={190} />
          </div>
        </div>
      </section>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Sparkles size={18} /> Lots par rareté</h3></div>
          <DonutChart ariaLabel="Lots tirés par rareté" formatValue={count} centerValue={count(t.draws)} centerLabel="lots tirés"
            slices={data.rarities.map((r) => ({
              key: r.rarity, label: rarityLabel(r.rarity), value: r.draws, color: rarityColor(r.index),
              sub: `prévu ${rateLabel(r.expectedRate)}`,
            }))} />
          <div className={lb.tableWrap}>
            <table className={lb.table}>
              <thead><tr><th>Rareté</th><th className={lb.num}>Lots</th><th className={lb.num}>Taux vu</th><th className={lb.num}>Taux prévu</th><th>Écart</th></tr></thead>
              <tbody>
                {data.rarities.map((r) => (
                  <tr key={r.rarity}>
                    <td><span className={bx.lotCell}><span className={styles.swatch} style={{ background: rarityColor(r.index) }} />{rarityLabel(r.rarity)}</span></td>
                    <td className={lb.num}>{count(r.draws)}</td>
                    <td className={lb.num}>{rateLabel(r.observedRate)}</td>
                    <td className={lb.num}>{rateLabel(r.expectedRate)}</td>
                    <td><VerdictPill v={verdict(r.draws, r.expectedDraws)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Crown size={18} /> Derniers lots légendaires</h3></div>
          {data.legendaries.length === 0 ? <p className={lb.empty}>Aucun lot légendaire sur la période.</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Quand</th><th>Joueur</th><th>Lot</th>{box == null && <th>Box</th>}</tr></thead>
                <tbody>
                  {data.legendaries.slice(0, 15).map((l) => (
                    <tr key={`${l.at}-${l.uuid}-${l.lot}`}>
                      <td className={bx.when}>{formatInstant(l.at)}</td>
                      <td><span className={lb.playerName}>{formatPlayerName(l.name, l.displayName, shortUuid(l.uuid))}</span></td>
                      <td title={l.lot}>{lotLabel(l)}</td>
                      {box == null && <td>{nameOf(l.box)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.legendaries.length > 15 && <p className={styles.muted}>Les 15 plus récents sur {count(data.legendaries.length)} lus.</p>}
        </section>
      </div>

      <section className={styles.block}>
        <div className={styles.blockHead}><h3><Gem size={18} /> Lots obtenus</h3></div>
        {data.lots.length === 0 ? <p className={lb.empty}>Aucun lot tiré sur la période.</p> : (
          <>
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead>
                  <tr>
                    <th>Lot</th><th>Rareté</th>{box == null && <th>Box</th>}<th className={lb.num}>Fois</th><th className={lb.num}>Joueurs</th>
                    <th className={lb.num}>Taux vu</th><th className={lb.num}>Taux prévu</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l: BoxLotRowDTO) => (
                    <tr key={`${l.box}|${l.lot}`}>
                      <td title={l.lot}>{lotLabel(l)}</td>
                      <td><span className={bx.lotCell}><span className={styles.swatch} style={{ background: rarityColor(l.rarityIndex) }} />{rarityLabel(l.rarity)}</span></td>
                      {box == null && <td>{nameOf(l.box)}</td>}
                      <td className={lb.num}>{count(l.draws)}</td>
                      <td className={lb.num}>{count(l.players)}</td>
                      <td className={lb.num}>{rateLabel(l.observedRate)}</td>
                      <td className={lb.num}>{rateLabel(l.expectedRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.lots.length > LOT_ROWS && (
              <button className={lb.buttonGhost} onClick={() => setAllLots((v) => !v)}>
                {allLots ? 'Voir moins de lots' : `Voir tous les lots (${count(data.lots.length)})`}
              </button>
            )}
            <p className={styles.muted}>Taux vu et prévu dans sa box, à chaque lot tiré. Les légendaires d'abord, puis les plus tirés.</p>
          </>
        )}
      </section>

      <div className={styles.grid2}>
        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Trophy size={18} /> Joueurs qui ouvrent le plus</h3></div>
          {data.topOpeners.length === 0 ? <p className={lb.empty}>Personne sur la période.</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Rang</th><th>Joueur</th><th className={lb.num}>Box</th><th className={lb.num}>Lots</th><th className={lb.num}>Légendaires</th></tr></thead>
                <tbody>
                  {data.topOpeners.map((o) => {
                    const m = medal(o.rank);
                    return (
                      <tr key={o.uuid}>
                        <td><span className={`${lb.rank} ${m ? lb[m] : ''}`}>{o.rank}</span></td>
                        <td><span className={lb.playerName}>{formatPlayerName(o.name, o.displayName, shortUuid(o.uuid))}</span></td>
                        <td className={lb.num}>{count(o.openings)}</td>
                        <td className={lb.num}>{count(o.draws)}</td>
                        <td className={lb.num}>{count(o.legendary)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className={styles.block}>
          <div className={styles.blockHead}><h3><Users size={18} /> Lots par box selon le grade</h3></div>
          {data.ranks.length === 0 ? <p className={lb.empty}>Personne sur la période.</p> : (
            <div className={lb.tableWrap}>
              <table className={lb.table}>
                <thead><tr><th>Grade</th><th className={lb.num}>Box</th><th className={lb.num}>Lots</th><th className={lb.num}>Lots par box</th></tr></thead>
                <tbody>
                  {data.ranks.map((r) => (
                    <tr key={r.rank}>
                      <td>{r.rank === '?' ? 'Inconnu' : r.rank}</td>
                      <td className={lb.num}>{count(r.openings)}</td>
                      <td className={lb.num}>{count(r.draws)}</td>
                      <td className={lb.num}>{r.drawsPerOpening.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className={styles.muted}>Grade qui donne ses avantages au joueur à l'ouverture ; « Inconnu » pour un joueur parti pendant l'animation.</p>
        </section>
      </div>

      {data.namesPartial && (
        <div className={lb.notice}><AlertCircle size={16} /><span>Certains joueurs n'ont pas encore de pseudo connu : leur uuid est affiché à la place.</span></div>
      )}
    </div>
  );
}

function VerdictPill({ v }: { v: Verdict | null }) {
  if (!v) return <span className={styles.muted}>Trop peu pour juger</span>;
  const cls = v.tone === 'ok' ? bx.verdictOk : v.tone === 'up' ? bx.verdictUp : bx.verdictDown;
  return <span className={`${bx.verdict} ${cls}`}>{v.label}</span>;
}
